import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpStatusCode, HttpContextToken, HttpXsrfTokenExtractor } from "@angular/common/http";
import { Observable, catchError, throwError } from "rxjs";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { LoggedInUserKeys, LoggedInUserService } from "../service/logged-in-user.service";

/** Backend message for a cookie-authenticated mutation without a valid X-XSRF-TOKEN. */
const CSRF_REJECTED_MESSAGE = "CSRF token missing or invalid";
const CSRF_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Set on a request whose caller handles its own failures: a 401 then does not clear the
 * session or redirect, and no toast is shown. Used where a 401 is an expected answer
 * (session upgrade, SSO code exchange) rather than "your session expired".
 */
export const SKIP_GLOBAL_ERROR_HANDLING = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly _toastrService: ToastrService,
    private readonly loggedInUserService: LoggedInUserService,
    private readonly xsrfTokenExtractor: HttpXsrfTokenExtractor
  ) { }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const skipErrorHandling = request.headers.has('X-Skip-Error-Interceptor');

    if (!navigator.onLine) {
      // this._toastrService.warning("You are offline. Waiting for network...");
      return new Observable<HttpEvent<unknown>>((observer) => {
        const handleOnline = () => {
          window.removeEventListener("online", handleOnline);
          // this._toastrService.success("Network restored. Resuming request...");
          next.handle(request).subscribe(observer);
        };
        window.addEventListener("online", handleOnline);
      });
    }

    return next.handle(request).pipe(
      catchError((error: ErrorRoot) => {
        // The 403 response re-sets XSRF-TOKEN, so one retry with the fresh value succeeds.
        // Angular's XSRF interceptor runs before this one, so set the header here.
        if (
          error?.status === HttpStatusCode.Forbidden &&
          error.error?.message === CSRF_REJECTED_MESSAGE &&
          !request.context.get(CSRF_RETRIED)
        ) {
          const xsrfToken = this.xsrfTokenExtractor.getToken();
          if (xsrfToken) {
            return this.intercept(
              request.clone({
                setHeaders: { "X-XSRF-TOKEN": xsrfToken },
                context: request.context.set(CSRF_RETRIED, true),
              }),
              next
            );
          }
        }

        if (request.context.get(SKIP_GLOBAL_ERROR_HANDLING)) {
          return throwError(() => error);
        }

        const loggedInUserStringValue = this.loggedInUserService.getLoggedInUser(
          LoggedInUserKeys.LOGGED_IN_USER
        );

        // A blocked account (e.g. data deletion started) is rejected before the
        // controller, so the 401 has no { statusCode } body - fall back to the
        // HTTP status, but only for a signed-in user so a failed login stays put.
        const isUnauthorized =
          error?.error?.statusCode === HttpStatusCode.Unauthorized ||
          (error?.status === HttpStatusCode.Unauthorized && this.loggedInUserService.isLoggedIn());

        if (error) {
          if (isUnauthorized) {
            if (
              loggedInUserStringValue?.userRole == "FRACTAL_HR" ||
              loggedInUserStringValue?.userRole == "FRACTAL_ADMIN"  || loggedInUserStringValue?.userRole=='CLIENT_HR'
            ) {
              this.router.navigate(["/admin/sign-in"]);
            } else {
              this.router.navigate(["/"]);
            }
            // this._toastrService.error(error.error.message, "Error");
            if (!skipErrorHandling) { // Add this check
              this._toastrService.error(
                error.error?.message || "Your session has ended. Please sign in again.",
                "Error"
              );
            }
            this.loggedInUserService.clearEntireStorage();
          } else if (error.error?.statusCode === HttpStatusCode.BadRequest) {
            // this._toastrService.error(error.error.message, "Error");
            if (!skipErrorHandling) { // Add this check
              this._toastrService.error(error.error.message, "Error");
            }
          } else if (!navigator.onLine) {

            // this._toastrService.warning("Network lost. Retrying when connected...");
            return new Observable<HttpEvent<unknown>>((observer) => {
              const handleOnline = () => {
                window.removeEventListener("online", handleOnline);
                // this._toastrService.success("Network restored. Retrying request...");
                next.handle(request).subscribe(observer);
              };
              window.addEventListener("online", handleOnline);
            });
          }
        }

        return throwError(() => error);
      })
    );
  }
}

export interface ErrorRoot {
  headers: Headers;
  status: number;
  statusCode: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: Error;
}

export interface Headers {
  normalizedNames: NormalizedNames;
  lazyUpdate;
}

export interface NormalizedNames {}

export interface Error {
  status: string;
  message: string;
  data;
  statusCode: number;
  value;
  msg;
  objectData;
}
